import logger from 'utils/logger';
import { PluginModel } from 'player/plugins/model';
import AdEvents from './tpaas/ad-events';
import PlaybackEvents from './tpaas/playback-events';
import { getPulseTracker } from '@schibsted/pulse-sdk';
import type {
    Tracker,
    IEngagementVideo,
    IEngagementAudio,
    IEngagementVideoAd,
    IProvider,
} from '@schibsted/pulse-sdk';
import type { Asset } from '@schibsted-svp/svp-api-types';
import { normalizeAspectRatio } from '../playback/provider';

type VideoAdEngagementType = Exclude<
    Exclude<IEngagementVideoAd['engagement'], undefined>['engagementType'],
    undefined
>;
type VideoEngagementType = Exclude<
    Exclude<IEngagementVideo['engagement'], undefined>['engagementType'],
    undefined
>;
type AudioEngagementType = Exclude<
    Exclude<IEngagementAudio['engagement'], undefined>['engagementType'],
    undefined
>;

type TpaasPluginOptions = {
    provider: string;
    sdkConfig: never;
    embed?: boolean;
    previousAssetId?: number;
    tts?: {
        enabled: boolean;
    };
    engagementIntervals?: {
        vod?: number;
        live?: number;
    };
};

type BrandedAsset = Asset & { vendor: string };

/**
 * Integration with TPaaS Pulse Tracker
 */
export class TpaasPlugin extends PluginModel<TpaasPluginOptions> {
    setupErrorEventEmitted: boolean = false;
    embed: boolean;
    isMockAsset: boolean;
    provider: string;
    previousAssetId: number | undefined;
    tracker: Tracker | null;
    playerAsset: BrandedAsset = null as unknown as BrandedAsset;
    sdkConfig: unknown; // TODO: needed
    intervals: {
        vod?: number;
        live?: number;
    };
    tts: { enabled: boolean };

    adEvents: AdEvents = null!;
    playbackEvents: PlaybackEvents = null!;
    casting: boolean = false;

    constructor(options: TpaasPluginOptions, isMockAsset: boolean) {
        super(options);

        this.onPlaybackEvent = this.onPlaybackEvent.bind(this);
        this.onAdEvent = this.onAdEvent.bind(this);

        this.provider = options.provider;
        this.sdkConfig = options.sdkConfig;
        this.intervals = {
            ...(options?.engagementIntervals?.live && {
                live: options.engagementIntervals.live,
            }),
            ...(options?.engagementIntervals?.vod && {
                vod: options?.engagementIntervals?.vod,
            }),
        };
        this.embed = options.embed === true;
        this.previousAssetId = options.previousAssetId;
        this.tracker = null;
        this.isMockAsset = isMockAsset;
        this.tts = options.tts || { enabled: false };
    }

    getName() {
        return 'Tpaas';
    }

    get jw() {
        return this.player.getJWPlayer();
    }

    getPlayType() {
        const autoplay = this.player.config.get('autoplay');
        if (autoplay === 'viewable') {
            return 'ScrollToPlay';
        }
        if (autoplay) {
            return 'Autoplay';
        }
        return 'ClickToPlay';
    }

    getDisplayMode() {
        const container = this.jw?.getContainer();
        const video = container?.querySelector('video');
        const isVideo = this.playerAsset.assetType === 'video';

        // TODO: not implemented "Overlay" [v] | "Minimized" [av] | "Carplay" [av] | "Floating" [v]

        if (this.jw?.getFullscreen()) {
            return 'Fullscreen';
        }
        if (isVideo && document.pictureInPictureElement === video) {
            return 'PictureInPicture';
        }
        // @ts-expect-error: property supported on Apple devices
        if (video?.webkitCurrentPlaybackTargetIsWireless) {
            return 'Airplay';
        }
        if (this.casting) {
            return 'Chromecast';
        }
        if (isVideo && this.player.getFloating()) {
            return 'Sticky';
        }
        if (document.visibilityState === 'hidden') {
            return 'Background';
        }
        return 'Normal';
    }

    getInteraction(data: {
        /**
         * The offset of the current playback from the start of the video in milliseconds.
         */
        positionMs: number;
        /**
         * The playback position in the video where the current play segment started. For the first segment it equals the start position, and after a seek it equals the seek-from position.
         */
        prevPositionMs: number;
        /**
         * The playback position (in milliseconds) the user initiated the seek (timeline jump) from. Only included when engagement.type = "seek"
         */
        seekFromMs?: number;
        /**
         * The playback position (in milliseconds) the user sought to. Only included when engagement.type = "seek"
         */
        seekToMs?: number;
        /**
         * How much time user has spent on the playback (in milliseconds) regardless of seeking, pausing etc.
         */
        viewtimeMs: number;
    }): IEngagementAudio['interaction'] | IEngagementVideo['interaction'] {
        const playbackSpeed = this.jw?.getPlaybackRate();
        return {
            ...data,
            displayMode: this.getDisplayMode(),
            isMuted: this.player.getMute(),
            playType: this.getPlayType(),
            ...(playbackSpeed !== undefined && { playbackSpeed }),
            ...(this.playerAsset.assetType === 'video' && {
                // loopCount: 0,
                quality: 'High', // TODO: map quality names
            }),
        };
    }

    async getTracker(): Promise<Tracker | null> {
        if (this.tracker === null) {
            return new Promise((resolve) =>
                this.once('ready', () => {
                    resolve(this.tracker);
                }),
            );
        }

        return this.tracker;
    }

    setup() {
        // prevent asset to be changed with playnext before stream completes
        this.playerAsset = this.player.getRawAsset() as BrandedAsset;

        this.adEvents = new AdEvents(this.player);
        this.playbackEvents = new PlaybackEvents(this.player, {
            vodInterval: this.intervals.vod,
            liveInterval: this.intervals.live,
        });

        // initialize only once
        // webpack freaks out and sometimes treats it as a module and sometimes as a default export
        getPulseTracker()
            .then((tracker) => {
                this.onReady(tracker);
            })
            .catch(() => {
                this.setupErrorEventEmitted = true;
                this.trigger('setupError');
            });

        this.jw?.on('cast', (e) => {
            console.log('casting', e.active);
            this.casting = e.active;
        });

        this.listenTo(this.adEvents, 'all', this.onAdEvent);
        this.listenTo(this.playbackEvents, 'all', this.onPlaybackEvent);
    }

    onReady(tracker: Tracker) {
        this.tracker = tracker;
        this.trigger('ready');
    }

    protected getProvider(): Omit<IProvider, 'providerId' | 'providerSdrn'> {
        return {
            product: 'tpaas-stats',
            stage: 'Pro',
            version: '1.0.0',
        };
    }

    onPlaybackEvent(
        type: 'Pause',
        data: {
            playMethod: 'interaction' | null;
            duration: number;
            position: number;
        },
    ): void;
    onPlaybackEvent(
        type: VideoEngagementType | AudioEngagementType,
        data: Record<string, unknown> = {},
    ): void {
        if (this.isMockAsset) {
            return;
        }

        logger('TPaaS').log(
            'onPlaybackEvent',
            type,
            JSON.stringify(data, null, 4),
        );

        const asset = this.playerAsset;
        const container = this.player.getContainer();
        const video = container.querySelector('video');
        const pulseConfig = this.player.config.get('pulse');

        const viewtimeMs = data.duration ? Number(data.duration) * 1000 : 0;
        const positionMs = data.position ? Number(data.position) * 1000 : 0;

        const interaction = this.getInteraction({
            positionMs,
            prevPositionMs: 0,
            ...(type === 'Seek' && {
                seekFromMs: data.seekFromMs as number | undefined,
                seekToMs: data.seekToMs as number | undefined,
            }),
            viewtimeMs,
        });

        const audioTracks = this.jw?.getAudioTracks();
        const additional = asset.additional || {};
        const accessControl = Object.keys(additional.access || {});

        // object
        const containerName = container?.tagName?.toLowerCase() ?? 'page';
        const durationMs = asset.streamType !== 'live' ? asset.duration : -1;
        const objectId = asset.id.toString();
        const objectSdrn = `sdrn:${asset.provider}:${asset.assetType}:${asset.id}`;
        const playerId = container?.id ?? '';
        const playlistId = undefined; // TODO
        const queuePosition = this.jw?.getPlaylistIndex();
        const variant = asset.additional.metadata?.['contentType'] ?? '';
        const imageUrl = asset.images.main;
        const isAccessible = audioTracks ? audioTracks.length > 0 : false;
        const category = asset.category.title;

        // track
        if (asset.assetType === 'video') {
            this.tracker?.trackEngagementVideo({
                engagement: {
                    ...(type !== 'Load' && { action: 'Click' }),
                    engagementType: type,
                },
                interaction: interaction as IEngagementVideo['interaction'],
                placement: {
                    containerName,
                },
                object: {
                    durationMs,
                    ...(pulseConfig?.elementSize && {
                        elementSize: pulseConfig.elementSize,
                    }),
                    name: asset.title,
                    objectId,
                    objectSdrn,
                    playerId,
                    playlistId,
                    ...(queuePosition && { queuePosition }),
                    streamType: asset.streamType,
                    variant,
                    category,
                    imageUrl,
                    isAccessible,
                    playTypeIntent: this.getPlayType(),
                    streamAccessLevel: accessControl.join(','),

                    player: 'VideoPlayer',
                    aspectRatio: normalizeAspectRatio(
                        video!.offsetWidth / video!.offsetHeight,
                    ),
                    isAd: false, // TODO
                    isLoop: this.jw?.getConfig().repeat ?? false,
                    isSponsored:
                        asset.additional.metadata?.sponsored === 'true',
                },
                provider: this.getProvider(),
            });
        } else if (type !== 'Prev') {
            this.tracker?.trackEngagementAudio({
                engagement: {
                    ...(type !== 'Load' && { action: 'Click' }),
                    engagementType: type,
                },
                interaction: interaction as IEngagementAudio['interaction'],
                placement: {
                    containerName,
                },
                object: {
                    durationMs,
                    ...(pulseConfig?.elementSize && {
                        elementSize: pulseConfig.elementSize,
                    }),
                    name: asset.title,
                    objectId,
                    objectSdrn,
                    playerId,
                    playlistId,
                    ...(queuePosition && { queuePosition }),
                    streamType: asset.streamType,
                    variant,
                    category,
                    imageUrl,
                    isAccessible,
                    playTypeIntent: this.getPlayType(),
                    streamAccessLevel: accessControl.join(','),

                    player: this.tts ? 'TtsPlayer' : 'AudioPlayer',
                },
                provider: this.getProvider(),
            });
        }
    }

    onAdEvent(
        type: VideoAdEngagementType,
        data: { id: string; position: number; [key: string]: unknown } = {
            id: '',
            position: 0,
        },
    ) {
        if (this.isMockAsset) {
            return;
        }
        return this.tracker?.trackEngagementVideoAd({
            engagement: {
                action: 'Click', // TODO: tmp val as it is currently required prop
                elementName: 'unknown', // TODO: no idea what this should be
                engagementType: type,
            },
            interaction: {
                positionMs: data.position && data.position * 1000, // TODO:
                isMuted: this.player.getMute(),
            },
            placement: {
                containerName: '', // TODO:
            },
            object: {
                adDurationMs: data.position ? data.position * 1000 : -1, // TODO:
                adFormat: '', // TODO:
                adId: data.id, // TODO: verify
                adSequencePosition: 0, // TODO:
                adSkipOffsetMs: undefined, // TODO:
                adSkippable: false, // TODO:
                adSlot: 'Preroll', // TODO:
                mediaAssetId: String(this.playerAsset.id),
                mediaAssetVariant:
                    this.playerAsset.additional.metadata?.contentType || '', // TODO: required?
                objectSdrn: `sdrn:${this.provider}:videoad:${data.id}`,
                playType: this.getPlayType(),
                playerId: '', // TODO: take from session
            },
            provider: this.getProvider(),
        });
    }

    destroy() {
        this.stopListening();

        this.adEvents.destroy();
        this.playbackEvents.destroy();
    }
}
